import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CommunityService from '../../lib/services/communityService';
import { useAuth } from '../../contexts/AuthContext';
import type { UserProfile, UserMessage, UserConnection } from '../../types';
import { ArrowLeft, Send, User, Info, Clock, MessageSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import SEO from '../../components/SEO';

// Criar uma instância do serviço
const communityService = new CommunityService();

/**
 * Página de conversa individual que permite ver e enviar mensagens
 */
const ConversationPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState<UserProfile | null>(null);
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<UserConnection | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  // Buscar perfil do outro usuário e verificar conexão
  useEffect(() => {
    const loadUserAndConnection = async () => {
      if (!username || !user?.id) return;
      
      setLoading(true);
      try {
        // Buscar perfil do usuário
        const profile = await communityService.getUserProfileByUsername(username);
        if (!profile) {
          toast.error('Usuário não encontrado');
          navigate('/messages');
          return;
        }
        setOtherUser(profile);
        
        // Verificar status da conexão
        if (profile.id) {
          console.log(`[Conversation] Verificando conexão entre ${user.id} e ${profile.id}`);
          
          // Verificar conexões nas duas direções (usuário atual como origem ou destino)
          let connection = await communityService.getConnectionStatus(user.id, profile.id);
          
          if (!connection || connection.status !== 'accepted') {
            console.log(`[Conversation] Primeira verificação: ${connection ? connection.status : 'sem conexão'}`);
            
            // Se não encontrou ou não está aceita, verificar na direção oposta
            connection = await communityService.getConnectionStatus(profile.id, user.id);
            console.log(`[Conversation] Segunda verificação: ${connection ? connection.status : 'sem conexão'}`);
          }
          
          setConnectionStatus(connection);
          
          // Se não houver conexão aceita, não permitir mensagens
          if (!connection || connection.status !== 'accepted') {
            console.log(`[Conversation] Conexão final: ${connection ? connection.status : 'sem conexão'}`);
            toast.error('Você precisa ser amigo deste usuário para enviar mensagens');
          } else {
            console.log(`[Conversation] Conexão aceita encontrada: ${connection.id}`);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar perfil:', error);
        toast.error('Erro ao carregar informações do usuário');
      } finally {
        setLoading(false);
      }
    };
    
    loadUserAndConnection();
  }, [username, user?.id, navigate]);
  
  // Buscar mensagens entre os usuários
  useEffect(() => {
    const loadMessages = async () => {
      if (!user?.id || !otherUser?.id) return;
      
      try {
        const chatMessages = await communityService.getMessages(user.id, otherUser.id);
        setMessages(chatMessages);
        
        // Marcar mensagens recebidas como lidas
        await communityService.markAllMessagesAsRead(otherUser.id, user.id);
      } catch (error) {
        console.error('Erro ao carregar mensagens:', error);
      }
    };
    
    if (otherUser?.id) {
      loadMessages();
      
      // Atualizar mensagens a cada 10 segundos
      const interval = setInterval(loadMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [user?.id, otherUser?.id]);
  
  // Rolar para a última mensagem quando novas mensagens chegarem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleGoBack = () => {
    navigate('/messages');
  };
  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !user?.id || !otherUser?.id) return;
    
    // Verificar se existe conexão aceita
    if (!connectionStatus || connectionStatus.status !== 'accepted') {
      toast.error('Você precisa ser amigo deste usuário para enviar mensagens');
      return;
    }
    
    setSending(true);
    try {
      const result = await communityService.sendMessage(user.id, otherUser.id, newMessage.trim());
      
      if (result.success && result.data) {
        // Adicionar mensagem à lista
        setMessages(prev => [...prev, result.data!]);
        setNewMessage('');
      } else {
        toast.error(result.error || 'Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };
  
  const formatMessageDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return `Hoje, ${format(date, 'HH:mm')}`;
      } else if (date.toDateString() === yesterday.toDateString()) {
        return `Ontem, ${format(date, 'HH:mm')}`;
      } else {
        return format(date, "dd 'de' MMMM, HH:mm", { locale: ptBR });
      }
    } catch (error) {
      return dateString;
    }
  };
  
  const viewUserProfile = () => {
    if (username) {
      navigate(`/profile/${username}`);
    }
  };
  
  return (
    <>
      <SEO 
        title={`Conversa com ${otherUser?.username || username || 'Usuário'}`}
        description="Troque mensagens com usuários do Boardverso."
      />
      <div className="w-full h-full">
        <div className="flex flex-col h-full pb-0">
          {/* Cabeçalho */}
          <div className="bg-white border-b px-4 py-3 flex items-center sticky top-0 z-10 dark:bg-gray-800 dark:border-gray-700">
            <button
              onClick={handleGoBack}
              className="mr-4 text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            
            {loading ? (
              <div className="flex-1 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-1 dark:bg-gray-700"></div>
                <div className="h-3 bg-gray-100 rounded w-16 dark:bg-gray-600"></div>
              </div>
            ) : (
              <div
                className="flex-1 flex items-center cursor-pointer"
                onClick={viewUserProfile}
              >
                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-800 mr-3 dark:bg-indigo-900/50 dark:text-indigo-300">
                  {otherUser?.avatar_url ? (
                    <img 
                      src={otherUser.avatar_url} 
                      alt={otherUser.username || 'Usuário'} 
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <h2 className="font-medium text-gray-900 dark:text-gray-100">
                    {otherUser?.full_name || otherUser?.username || 'Usuário'}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    @{otherUser?.username || 'usuario'}
                  </p>
                </div>
              </div>
            )}
            
            <button
              onClick={viewUserProfile}
              className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            >
              <Info className="h-5 w-5" />
            </button>
          </div>
          
          {/* Container de mensagens */}
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900"
          >
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-500"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 px-4">
                <div className="p-4 mx-auto rounded-full bg-gray-100 inline-flex items-center justify-center mb-3 dark:bg-gray-700">
                  <MessageSquare className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">Nenhuma mensagem</h3>
                <p className="text-gray-600 mb-4 dark:text-gray-400">
                  Inicie uma conversa enviando uma mensagem abaixo.
                </p>
              </div>
            ) : (
              <>
                {/* Mensagens */}
                {messages.map((message, index) => {
                  const isUserMessage = message.sender_id === user?.id;
                  const showDate = index === 0 || (index > 0 && 
                    new Date(message.created_at).toDateString() !== 
                    new Date(messages[index - 1].created_at).toDateString()
                  );
                  
                  return (
                    <React.Fragment key={message.id}>
                      {showDate && (
                        <div className="flex justify-center my-4">
                          <div className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {formatMessageDate(message.created_at).split(',')[0]}
                          </div>
                        </div>
                      )}
                      
                      <div className={`flex ${isUserMessage ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`
                            max-w-[80%] px-4 py-2 rounded-lg relative
                            ${isUserMessage
                              ? 'bg-indigo-500 text-white rounded-tr-none dark:bg-indigo-600'
                              : 'bg-white text-gray-800 rounded-tl-none border border-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600'
                            }
                          `}
                        >
                          <p>{message.content}</p>
                          <div className={`
                            text-xs mt-1 flex items-center
                            ${isUserMessage ? 'text-indigo-100 dark:text-indigo-200' : 'text-gray-500 dark:text-gray-400'}
                          `}>
                            <Clock className="h-3 w-3 mr-1" />
                            {formatMessageDate(message.created_at).split(',')[1]}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
          
          {/* Formulário de envio */}
          <div className="bg-white border-t px-4 py-3 sticky bottom-0 dark:bg-gray-800 dark:border-gray-700">
            {connectionStatus?.status !== 'accepted' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center dark:bg-yellow-900/20 dark:border-yellow-700">
                <p className="text-yellow-800 text-sm dark:text-yellow-300">
                  Você precisa ser amigo deste usuário para trocar mensagens.
                </p>
                <button
                  onClick={viewUserProfile}
                  className="mt-2 text-indigo-600 text-sm font-medium hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Ver perfil
                </button>
              </div>
            ) : (
              <form onSubmit={handleSendMessage} className="flex items-center">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Digite sua mensagem..."
                  className="flex-1 border border-gray-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
                  disabled={sending}
                />
                <button
                  type="submit"
                  className={`
                    bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-r-lg
                    flex items-center justify-center
                    dark:bg-indigo-500 dark:hover:bg-indigo-600
                    ${sending ? 'opacity-70 cursor-wait' : ''}
                  `}
                  disabled={sending || !newMessage.trim()}
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ConversationPage; 